import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useRequestPasswordReset } from '@/data/auth';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

const schema = z.object({ email: z.string().email('Bitte gültige E-Mail.') });
type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  useDocumentTitle('Passwort vergessen');
  const requestReset = useRequestPasswordReset();
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    await requestReset.mutateAsync(values);
    toast.success('Wenn das Konto existiert, wurde eine E-Mail verschickt.');
  };

  return (
    <>
      <h1 className="text-2xl font-semibold">Passwort vergessen</h1>
      <p className="mt-1 text-sm text-[var(--muted-foreground)]">
        Gib deine E-Mail ein – wir senden dir einen Reset-Link.
      </p>
      <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-1.5">
          <Label htmlFor="email">E-Mail</Label>
          <Input id="email" type="email" autoComplete="email" {...register('email')} />
          {errors.email && <p className="text-xs text-[var(--destructive)]">{errors.email.message}</p>}
        </div>
        <Button type="submit" className="w-full" disabled={requestReset.isPending}>
          {requestReset.isPending ? <Spinner /> : 'Reset-Link senden'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-[var(--muted-foreground)]">
        <Link to="/login" className="font-medium text-ocean-700 hover:underline">Zurück zum Login</Link>
      </p>
    </>
  );
}
